import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, MenuItem, Typography, Stack, Button, IconButton, Divider, Paper, Grid } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import useFetchApplicationsAndQuestionnaires from "../hooks/useFetchApplicationsAndQuestionnaires";

const toEpoch = (dateString) => (dateString ? Math.floor(new Date(dateString).getTime() / 1000) : null);

const toLocalInputValue = (value) => {
  if (!value && value !== 0) return "";
  let parsedDate;
  if (typeof value === "number") parsedDate = new Date(value * (value < 1e12 ? 1000 : 1));
  else if (typeof value === "string") {
    const onlyDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (onlyDate) {
      const [, y, m, day] = onlyDate.map(Number);
      parsedDate = new Date(y, m - 1, day, 12, 0, 0, 0);
    } else {
      parsedDate = new Date(value);
      parsedDate.setHours(12, 0, 0, 0);
    }
  } else return "";
  if (isNaN(parsedDate)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const year = parsedDate.getFullYear();
  const month = pad(parsedDate.getMonth() + 1);
  const day = pad(parsedDate.getDate());
  const hours = pad(parsedDate.getHours());
  const minutes = pad(parsedDate.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function buildAssignPayload({ userIds, appId, selectedQuestionnaireItems, activationStart, activationEnd }) {
  return {
    assignapp: {
      user_ids: userIds,
      app_id: appId,
      questionnaires: selectedQuestionnaireItems.map((it) => ({
        questionnaire_id: it.questionnaireId,
        context: it.context || null,
      })),
      activation_window_start: toEpoch(activationStart),
      activation_window_end: toEpoch(activationEnd),
    },
  };
}

const AssignmentPicker = ({ value, onChange, disabled = false, busy = false, errorHook }) => {
  const { data, loading, error } = useFetchApplicationsAndQuestionnaires();
  const applications = useMemo(() => data?.applications ?? [], [data]);
  const questionnaires = useMemo(() => data?.questionnaires ?? [], [data]);

  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [questionnaireInput, setQuestionnaireInput] = useState("");
  const [contextInput, setContextInput] = useState("");

  const appIdNum = value.appId === "" ? null : Number(value.appId);
  const selectedApp = useMemo(() => applications.find((a) => Number(a.id) === appIdNum) || null, [applications, appIdNum]);

  const filteredQuestionnaires = useMemo(() => questionnaires.filter((q) => q.aplicacione_id === appIdNum), [questionnaires, appIdNum]);

  useEffect(() => {
    if (!selectedApp) {
      onChange({ ...value, activationStart: "", activationEnd: "", selectedQuestionnaireItems: [] });
      return;
    }
    const startDefault = selectedApp.date_min ?? selectedApp.activation_window_start ?? "";
    const endDefault = selectedApp.date_max ?? selectedApp.activation_window_end ?? "";
    onChange({
      ...value,
      activationStart: toLocalInputValue(startDefault),
      activationEnd: toLocalInputValue(endDefault),
      selectedQuestionnaireItems: [],
    });
    setContextInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp?.id]);

  const windowError = useMemo(() => {
    const startTime = value.activationStart && new Date(value.activationStart).getTime();
    const endTime = value.activationEnd && new Date(value.activationEnd).getTime();
    if (startTime && endTime && endTime <= startTime) return "Ventana inválida: el fin debe ser mayor que el inicio.";
    return "";
  }, [value.activationStart, value.activationEnd]);

  const minBound = toLocalInputValue(selectedApp?.date_min ?? selectedApp?.activation_window_start ?? "");
  const endOfDay = (v) => (v ? `${v.slice(0, 10)}T23:59` : "");
  const maxBound = selectedApp?.date_max
    ? endOfDay(toLocalInputValue(selectedApp.date_max))
    : toLocalInputValue(selectedApp?.activation_window_end ?? "");

  const handleAddQuestionnaireItem = () => {
    if (!selectedQuestionnaire) {
      errorHook?.("Selecciona un cuestionario.");
      return;
    }
    const questionnaireIdNum = Number(selectedQuestionnaire.id ?? selectedQuestionnaire.questionnaire_id ?? selectedQuestionnaire.questionnaireId);
    const questionnaireObj =
      questionnaires.find((q) => Number(q.id) === questionnaireIdNum) ||
      questionnaires.find((q) => Number(q.questionnaire_id) === questionnaireIdNum);

    const next = [
      ...(value.selectedQuestionnaireItems ?? []),
      {
        questionnaireId: questionnaireIdNum,
        title: questionnaireObj?.title ?? questionnaireObj?.name ?? `Cuestionario ${questionnaireIdNum}`,
        context: contextInput?.trim() || "",
      },
    ];
    onChange({ ...value, selectedQuestionnaireItems: next });
    setSelectedQuestionnaire(null);
    setQuestionnaireInput("");
    setContextInput("");
  };

  const handleRemoveQuestionnaireItem = (idx) => {
    const next = (value.selectedQuestionnaireItems ?? []).filter((_, i) => i !== idx);
    onChange({ ...value, selectedQuestionnaireItems: next });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Aplicación */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" mb={1} color="text.secondary">
            Seleccione la aplicación:
          </Typography>
          <TextField
            size="small"
            select
            fullWidth
            label="Aplicación"
            value={value.appId ?? ""}
            onChange={(e) => onChange({ ...value, appId: e.target.value === "" ? "" : Number(e.target.value) })}
            disabled={disabled || busy || loading || !!error}
            required
          >
            {applications.map((application) => (
              <MenuItem key={application.id} value={Number(application.id)}>
                {application.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Selector de cuestionarios */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" mb={1} color="text.secondary">
            Seleccionar cuestionarios:
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <Autocomplete
              value={selectedQuestionnaire}
              onChange={(e, newValue) => setSelectedQuestionnaire(newValue)}
              inputValue={questionnaireInput}
              onInputChange={(e, newInputValue) => setQuestionnaireInput(newInputValue)}
              options={filteredQuestionnaires}
              getOptionLabel={(option) => option?.title ?? ""}
              isOptionEqualToValue={(option, v) => Number(option.id ?? option.questionnaire_id) === Number(v?.id ?? v?.questionnaire_id)}
              loading={loading}
              disabled={!value.appId || disabled || busy || loading || !!error}
              fullWidth
              renderInput={(params) => <TextField {...params} size="small" label="Cuestionario" placeholder="Escriba para buscar…" />}
              openOnFocus
              clearOnBlur={false}
              blurOnSelect
            />
            {/* Contexto opcional */}
            <TextField
              size="small"
              label="Contexto (opcional)"
              placeholder="p. ej. Curso 1, Curso 2..."
              value={contextInput}
              onChange={(e) => setContextInput(e.target.value)}
              disabled={!value.appId || disabled || busy || loading || !!error}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleAddQuestionnaireItem}
              disabled={!value.appId || !selectedQuestionnaire || disabled || busy || loading || !!error}
              sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap", flex: "0 0 auto" }}
            >
              Agregar a la lista
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Lista cuestionarios */}
      <Box mt={2}>
        <Typography variant="subtitle2" mb={1} color="text.secondary">
          Cuestionarios seleccionados:
        </Typography>

        <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, borderColor: "grey.200" }}>
          {(value.selectedQuestionnaireItems?.length ?? 0) === 0 ? (
            <Typography color="text.disabled" sx={{ px: 1, py: 1 }}>
              Aún no ha seleccionado ningún cuestionario
            </Typography>
          ) : (
            <Box
              sx={{
                maxHeight: { xs: 150, md: 200 },
                overflowY: "auto",
                pr: 1,
              }}
            >
              {value.selectedQuestionnaireItems.map((it, idx) => (
                <Box key={`${it.questionnaireId}-${idx}`}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 1,
                      px: 1,
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600}>{it.title}</Typography>
                      {!!it.context && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          Contexto: <strong>{it.context}</strong>
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveQuestionnaireItem(idx)} aria-label="Eliminar">
                      <DeleteIcon sx={{ color: (theme) => theme.palette.error.light }} fontSize="small" />
                    </IconButton>
                  </Box>
                  {idx < value.selectedQuestionnaireItems.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Ventana de tiempo */}
      <Box mt={2}>
        <Typography variant="subtitle2" mb={2} color="text.secondary">
          Defina la ventana de tiempo:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              type="datetime-local"
              label="Inicio ventana"
              fullWidth
              value={value.activationStart || ""}
              onChange={(e) => onChange({ ...value, activationStart: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              error={!!windowError}
              helperText={windowError || ""}
              inputProps={{ min: minBound || undefined, max: maxBound || undefined }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              type="datetime-local"
              label="Fin ventana"
              fullWidth
              value={value.activationEnd || ""}
              onChange={(e) => onChange({ ...value, activationEnd: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              error={!!windowError}
              helperText={windowError || ""}
              inputProps={{ min: value.activationStart || minBound || undefined, max: maxBound || undefined }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AssignmentPicker;
